import type { EstadoTaquilla, LineaPedida, VentaTaquilla } from '../../api/ops/pedidos';
import type { VentaSinConexion } from '../../api/ops/pases';

/**
 * Reglas de la venta en taquilla (10.7), sin pantalla: qué se puede añadir, cuántas quedan, los nombres de los
 * asistentes (G3), cómo recibe las entradas y lo que se manda a `createBoxOfficeSale`. El importe nunca se calcula aquí:
 * lo da la API (`quoteTicketOrder` antes de cobrar, el pedido después).
 */
export type Carrito = Readonly<Record<string, number>>;
export type FormaCobro = 'cash' | 'card_terminal';
export type Entrega = 'ya' | 'movil' | 'email';

type Vendible = EstadoTaquilla['sellable'][number];

/**
 * Cómo puede recibir las entradas: «Entra ya» solo en la tablet emparejada (G899), porque la API responde
 * `422 device_required` a `enter_now` desde una sesión sin tablet. Sin conexión (solo en la tablet), también «Al móvil» y
 * «Por email»: la venta del lote de la puerta lleva el contacto (`OfflineSaleItem.contact`) y se envía al volver la
 * conexión (G970).
 */
export function entregasPosibles(enLaTablet: boolean, sinConexion = false): readonly Entrega[] {
    if (sinConexion && !enLaTablet) {
        return [];
    }
    return enLaTablet ? ['ya', 'movil', 'email'] : ['movil', 'email'];
}

/** Entradas en el carrito. */
export function enCarrito(c: Carrito): number {
    return Object.values(c).reduce((n, x) => n + x, 0);
}

/** Cuántas se pueden vender ahora: lo que dice la API (`available`). */
export function quedan(estado: Pick<EstadoTaquilla, 'available'>): number {
    return Math.max(0, estado.available);
}

/** Se puede añadir una más de este tipo: queda de ese tipo y del total. */
export function sePuedeAnadir(c: Carrito, tipo: Vendible, estado: Pick<EstadoTaquilla, 'available'>): boolean {
    return (c[tipo.ticket_type_id] ?? 0) < tipo.available && enCarrito(c) < quedan(estado);
}

export function anadir(c: Carrito, tipo: Vendible, estado: Pick<EstadoTaquilla, 'available'>): Carrito {
    return sePuedeAnadir(c, tipo, estado) ? { ...c, [tipo.ticket_type_id]: (c[tipo.ticket_type_id] ?? 0) + 1 } : c;
}

export function quitar(c: Carrito, idTipo: string): Carrito {
    const n = (c[idTipo] ?? 0) - 1;
    if (n > 0) {
        return { ...c, [idTipo]: n };
    }
    return Object.fromEntries(Object.entries(c).filter(([k]) => k !== idTipo));
}

/** «2 × General · 1 × Con consumición», en el orden de las baldosas. */
export function resumenCarrito(c: Carrito, tipos: readonly Vendible[]): string {
    const partes = tipos.filter((t) => (c[t.ticket_type_id] ?? 0) > 0).map((t) => `${String(c[t.ticket_type_id] ?? 0)} × ${t.name}`);
    return partes.length === 0 ? 'Ninguna entrada todavía' : partes.join(' · ');
}

/** «Taquilla · 21:45»: el nombre de una entrada nominativa sin nombre (G3). */
export function nombrePorDefecto(ahora: Date): string {
    return `Taquilla · ${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
}

/** Los campos de nombre de un evento nominativo: uno por entrada («Asistente 1 · General»). */
export function camposDeNombre(c: Carrito, tipos: readonly Vendible[]): { clave: string; tipo: string; etiqueta: string }[] {
    const campos: { clave: string; tipo: string; etiqueta: string }[] = [];
    for (const t of tipos) {
        for (let i = 0; i < (c[t.ticket_type_id] ?? 0); i++) {
            campos.push({ clave: `${t.ticket_type_id}-${String(i)}`, tipo: t.ticket_type_id, etiqueta: `Asistente ${String(campos.length + 1)} · ${t.name}` });
        }
    }
    return campos;
}

/** Las líneas para la API (con los nombres si es nominativo; sin nombre, «Taquilla · HH:MM»). */
export function lineasDeVenta(c: Carrito, tipos: readonly Vendible[], nominativo: boolean, nombres: Readonly<Record<string, string>>, ahora: Date): LineaPedida[] {
    return tipos
        .filter((t) => (c[t.ticket_type_id] ?? 0) > 0)
        .map((t) => {
            const n = c[t.ticket_type_id] ?? 0;
            const linea: LineaPedida = { ticket_type_id: t.ticket_type_id, quantity: n };
            if (nominativo) {
                linea.holder_names = Array.from({ length: n }, (_, i) => nombres[`${t.ticket_type_id}-${String(i)}`]?.trim() || nombrePorDefecto(ahora));
            }
            return linea;
        });
}

export const emailValido = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
export const movilValido = (v: string) => v.replace(/\D/g, '').length >= 9;

/** Prefijo internacional por país del negocio (los mismos que usa Clientes en el servidor, G320). */
const PREFIJOS: Readonly<Record<string, string>> = {
    ES: '34', PT: '351', FR: '33', IT: '39', DE: '49', GB: '44', IE: '353', NL: '31', BE: '32', CH: '41', AT: '43', AD: '376',
    MX: '52', AR: '54', CO: '57', CL: '56', PE: '51', US: '1', CA: '1', MA: '212',
};
/** Países cuyo número nacional empieza por un 0 que no va en E.164 (en Italia sí va). */
const CON_CERO_NACIONAL = new Set(['GB', 'FR', 'DE', 'NL', 'BE', 'CH', 'AT', 'IE', 'PT']);

/**
 * El móvil como se envía (G975, ADR-0247): sin espacios ni guiones, `00` como `+` y, si se escribió sin prefijo, con el del
 * país del negocio («612 345 678» en un negocio de España → `+34612345678`). Sin país conocido, tal como se escribió.
 */
export function movilConPrefijo(v: string, pais: string | null): string {
    let limpio = v.trim().replace(/[\s\-()./]/g, '');
    if (limpio.startsWith('00')) {
        limpio = `+${limpio.slice(2)}`;
    }
    const codigo = pais === null ? undefined : pais.toUpperCase();
    const prefijo = codigo === undefined ? undefined : PREFIJOS[codigo];
    if (limpio.startsWith('+') || prefijo === undefined || codigo === undefined || !/^[0-9]+$/.test(limpio)) {
        return limpio;
    }
    return `+${prefijo}${CON_CERO_NACIONAL.has(codigo) ? limpio.replace(/^0+/, '') : limpio}`;
}

/**
 * Sin conexión, el contacto se comprueba ya como lo comprobará Pases (`OfflineSaleItem.contact`: teléfono E.164 y email de
 * hasta 254 caracteres, G970), con el prefijo del país del negocio ya puesto (G975). Si aun así no vale, Pases acepta la
 * venta sin él (`contact_discarded`, G974).
 */
export const movilValidoSinConexion = (v: string, pais: string | null = null) => /^\+[1-9][0-9]{6,14}$/.test(movilConPrefijo(v, pais));
export const emailValidoSinConexion = (v: string) => emailValido(v) && v.trim().length <= 254;

export interface Errores {
    entradas: string | null;
    forma: string | null;
    entrega: string | null;
    contacto: string | null;
}

/** Lo que falta para cobrar, con los textos del diseño. */
export function erroresDeVenta(c: Carrito, forma: FormaCobro | null, entrega: Entrega | null, contacto: string, sinConexion = false, pais: string | null = null): Errores {
    const [movilOk, emailOk] = sinConexion ? [(v: string) => movilValidoSinConexion(v, pais), emailValidoSinConexion] : [movilValido, emailValido];
    return {
        entradas: enCarrito(c) === 0 ? 'Elige al menos una entrada.' : null,
        forma: forma === null ? 'Elige efectivo o datáfono.' : null,
        entrega: entrega === null ? 'Elige cómo recibe las entradas.' : null,
        contacto: entrega === 'movil' && !movilOk(contacto) ? 'Escribe un móvil válido.' : entrega === 'email' && !emailOk(contacto) ? 'Escribe un email válido.' : null,
    };
}

export function hayErrores(e: Errores): boolean {
    return Object.values(e).some((x) => x !== null);
}

/**
 * Lo que se manda a `createBoxOfficeSale` (y, sin conexión, el contacto de la venta guardada). El teléfono va sin espacios y
 * con el prefijo del país del negocio si se escribió sin él (G975, `movilConPrefijo`). «Entra ya» manda `enter_now`:
 * Pases deja los pases validados en esta tablet (G832).
 */
export function venta(lineas: LineaPedida[], forma: FormaCobro, entrega: Entrega, contacto: string, pais: string | null = null): VentaTaquilla {
    const v: VentaTaquilla = { lines: lineas, payment_method: forma, enter_now: entrega === 'ya' };
    if (entrega === 'movil') {
        v.contact = { phone: movilConPrefijo(contacto, pais) };
    } else if (entrega === 'email') {
        v.contact = { email: contacto.trim() };
    }
    return v;
}

/**
 * Línea de debajo de «Puedes vender N entradas» (10.7): con conexión en la tablet, su parte del cupo («Cupo de esta tablet:
 * quedan 6 de 15», `device_quota`, G786) junto al cupo de taquilla del evento; desde el panel, solo el del evento.
 */
export function lineaDelCupo(estado: Pick<EstadoTaquilla, 'quota' | 'device_quota'>): string | null {
    const q = estado.quota;
    const delEvento = q == null ? null : `${String(Math.max(0, q.quantity - q.sold - q.returned))} de ${String(q.quantity)} del cupo de taquilla sin vender`;
    if (estado.device_quota != null) {
        const d = estado.device_quota;
        const tablet = `Cupo de esta tablet: quedan ${String(Math.max(0, d.quantity - d.used))} de ${String(d.quantity)}`;
        return delEvento === null ? tablet : `${tablet} · ${delEvento}`;
    }
    return delEvento;
}

/** Lo que dice «Vendido» según cómo recibe las entradas. */
export function textoVendido(entrega: Entrega, contacto: string): string {
    if (entrega === 'movil') {
        return `Las entradas le llegan ahora a ${contacto.trim()} por WhatsApp.`;
    }
    if (entrega === 'email') {
        return `Las entradas le llegan ahora a ${contacto.trim()} por email.`;
    }
    return 'Pueden pasar: la puerta registra su llegada al leer el pase.';
}

/**
 * La taquilla **sin conexión** en la tablet (10.7; G2, G4, G509): la da la copia de la puerta de esta tablet (6.2), que
 * trae su parte del cupo de taquilla y lo que se vende sin conexión con su precio. Sin copia de este evento, `caja` es nula.
 * La venta se guarda en la tablet (`vender`) y se envía sola, en el lote de la puerta, al volver la conexión.
 */
export interface ArticuloSinConexion {
    ref: { type: string; id: string };
    label: string;
    price: { amount: number; currency: string };
    nominative: boolean;
}

export interface CajaSinConexion {
    /** El evento de la copia (`access_group.ref`). */
    evento: string;
    nombre: string;
    /** La parte del cupo de esta tablet. */
    cupo: number;
    /** Lo que queda de esa parte, ya descontado lo vendido y aún sin enviar. */
    quedan: number;
    /** Ventas guardadas en la tablet y aún sin enviar. */
    sinEnviar: number;
    articulos: readonly ArticuloSinConexion[];
}

export interface VentaGuardada {
    lineas: { ref: { type: string; id: string }; quantity: number; holder_names?: string[] }[];
    forma: FormaCobro;
    total: { amount: number; currency: string };
    entraYa: boolean;
    /** «Al móvil» o «Por email» (G970): a quién se envían las entradas al volver la conexión. */
    contacto?: VentaSinConexion['contact'];
}

export interface TaquillaSinConexion {
    conConexion: boolean;
    caja: CajaSinConexion | null;
    vender: (v: VentaGuardada) => void;
    /** Ventas del último envío cuyo contacto no valía (`contact_discarded`, G974): sus entradas no se han enviado. */
    sinContacto?: number;
    olvidarSinContacto?: () => void;
    /** País del negocio de la copia de la puerta (`DoorPackageContent.business_country`, G986). */
    pais?: string | null;
}

/** El estado de la taquilla con la copia (lo mismo que daría `getBoxOfficeStatus`, con la parte de esta tablet). */
export function estadoSinConexion(caja: CajaSinConexion): EstadoTaquilla {
    return {
        event_id: caja.evento,
        sellable: caja.articulos.map((a) => ({ ticket_type_id: a.ref.id, name: a.label, price: a.price, available: caja.quedan, color: null })),
        available: caja.quedan,
        quota: null,
        device_quota: null,
    };
}

/** Sin conexión el importe no se puede pedir a la API: es el precio de la copia por las entradas elegidas. */
export function importeSinConexion(c: Carrito, caja: CajaSinConexion): { amount: number; currency: string } {
    const amount = caja.articulos.reduce((n, a) => n + a.price.amount * (c[a.ref.id] ?? 0), 0);
    return { amount, currency: caja.articulos[0]?.price.currency ?? 'EUR' };
}

/** Las líneas de la venta sin conexión, con la referencia que dio Eventos a Pases al preparar la puerta. */
export function lineasSinConexion(lineas: readonly LineaPedida[], caja: CajaSinConexion): VentaGuardada['lineas'] {
    return lineas.flatMap((l) => {
        const a = caja.articulos.find((x) => x.ref.id === l.ticket_type_id);
        return a === undefined ? [] : [{ ref: a.ref, quantity: l.quantity, ...(l.holder_names === undefined ? {} : { holder_names: l.holder_names }) }];
    });
}

/** La franja «Sin conexión» de 10.7: lo que queda de la parte de esta tablet o que se acabó. */
export function textoSinConexion(caja: CajaSinConexion): string {
    return caja.quedan > 0
        ? `Vendes solo de la parte del cupo de taquilla de esta tablet: quedan ${String(caja.quedan)} de ${String(caja.cupo)}. Las ventas se envían solas al volver la conexión y lo que no vendas vuelve al aforo.`
        : `Se acabó la parte del cupo de esta tablet (${String(caja.cupo)}). Sin conexión no puedes vender más aquí, aunque otra tablet tenga cupo. Al volver la conexión vendes del aforo.`;
}

/** Debajo de «Quedan N entradas» sin conexión. */
export function lineaSinConexion(caja: CajaSinConexion): string {
    return `de las ${String(caja.cupo)} de esta tablet · ${String(caja.sinEnviar)} ${caja.sinEnviar === 1 ? 'venta' : 'ventas'} sin enviar`;
}

/** La ayuda del móvil del comprador: sin conexión, que se envía al volver la conexión (G970). */
export function ayudaDelMovil(sinConexion: boolean): string {
    return sinConexion ? 'Se guarda y se envía al volver la conexión.' : 'Le llegan por WhatsApp.';
}

/**
 * El aviso «Entradas sin enviar» de las ventas sin conexión que llegaron con un contacto que no valía (G974, G984): la venta
 * está hecha, sin envío. Sale en la pantalla que envió el lote, la Taquilla o la franja de la Puerta (G985; texto de C.25).
 */
export const TITULO_SIN_CONTACTO = 'Entradas sin enviar';
export function textoSinContacto(n: number): string {
    return n === 1
        ? 'No se han podido enviar las entradas de 1 venta: el contacto no era válido. La venta sí se ha guardado.'
        : `No se han podido enviar las entradas de ${String(n)} ventas: el contacto no era válido. Las ventas sí se han guardado.`;
}

/** Lo que dice «Vendido» de una venta guardada en la tablet. */
export function textoVendidoSinConexion(entrega: Entrega): string {
    return `Guardado en este dispositivo. Se confirma solo al volver la conexión${entrega === 'ya' ? '; su llegada ya cuenta.' : ' y entonces se envían las entradas.'}`;
}
